import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { shortTitle, shortDescription } from "@/utils/metaLength";
import { getLangFromUrl, setLangInUrl, useHreflang, updateCanonical, useAutoSyncLangToUrl } from "@/utils/seoLang";
import axios from "axios";
import { toast } from "sonner";
import { CITIES } from "@/cityConfig";
import { convertPrice, getCurrencyByCode } from "@/currencyConfig";
import { trackConversion } from "@/utils/analytics";
import { trackPageView, trackClick } from "@/utils/tracker";
import { t } from "@/translations";
import { HelpCircle } from "lucide-react";

import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import CategoriesSection from "@/components/CategoriesSection";
import ProductsSection from "@/components/ProductsSection";
import AdvantagesSection from "@/components/AdvantagesSection";
import HowWeWorkSection from "@/components/HowWeWorkSection";
import ReviewsSection from "@/components/ReviewsSection";
import GoogleReviewCta from "@/components/GoogleReviewCta";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import ProductPreview from "@/components/ProductPreview";
import GuaranteesSection from "@/components/GuaranteesSection";
import { DistrictJsonLd } from "@/components/JsonLd";
import { districtCatalog } from "@/utils/districtCatalog";
import { sorrentoDistrictContent } from "@/data/sorrentoDistricts";
import { dubaiDistrictContent } from "@/data/dubaiDistricts";
import { stMoritzDistrictContent } from "@/data/stMoritzDistricts";
import { zermattDistrictContent } from "@/data/zermattDistricts";
import { verbierDistrictContent } from "@/data/verbierDistricts";
import { sanSebastianDistrictContent } from "@/data/sanSebastianDistricts";
import { marbellaDistrictContent } from "@/data/marbellaDistricts";
import { granCanariaDistrictContent } from "@/data/granCanariaDistricts";
import { madridDistrictContent } from "@/data/madridDistricts";
import { sevilleDistrictContent } from "@/data/sevilleDistricts";
import { portoDistrictContent } from "@/data/portoDistricts";
import { lagosDistrictContent } from "@/data/lagosDistricts";
import { casablancaDistrictContent } from "@/data/casablancaDistricts";
import { parisDistrictContent } from "@/data/parisDistricts";
import { marrakechDistrictContent } from "@/data/marrakechDistricts";
import { londonDistrictContent } from "@/data/londonDistricts";
import { romeDistrictContent } from "@/data/romeDistricts";

const DEFAULT_LANGS = ["ru", "en"];

const DISTRICT_EXTRA_CONTENT = { dubai: dubaiDistrictContent, "st-moritz": stMoritzDistrictContent, zermatt: zermattDistrictContent, verbier: verbierDistrictContent, "san-sebastian": sanSebastianDistrictContent, marbella: marbellaDistrictContent, "gran-canaria": granCanariaDistrictContent, madrid: madridDistrictContent, seville: sevilleDistrictContent, porto: portoDistrictContent, lagos: lagosDistrictContent, casablanca: casablancaDistrictContent, paris: parisDistrictContent, marrakech: marrakechDistrictContent, london: londonDistrictContent, rome: romeDistrictContent };

const API = "/api";

function DistrictSeoBlock({ districtSeo, testId = "district-seo-text" }) {
  if (!districtSeo?.seoText) return null;
  // Render simple markdown-like text: headings (###/####), lists, tables, **bold**, [text](url), images
  const IMG_RE = /^!\[([^\]]*)\]\(([^)]+)\)$/;
  const renderInline = (text) => {
    const parts = [];
    const regex = /(!\[[^\]]*\]\([^)]+\)|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = regex.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      const tok = m[0];
      if (tok.startsWith("![")) {
        const im = tok.match(IMG_RE);
        if (im) {
          parts.push(
            <img
              key={`i${key++}`}
              src={im[2]}
              alt={im[1]}
              loading="lazy"
              className="rounded-xl shadow-sm my-6 w-full object-cover"
            />
          );
        }
      } else if (tok.startsWith("**")) {
        parts.push(<strong key={`b${key++}`}>{tok.slice(2, -2)}</strong>);
      } else {
        const linkMatch = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
          parts.push(
            <a key={`a${key++}`} href={linkMatch[2]} className="text-pink-600 hover:underline font-medium">
              {linkMatch[1]}
            </a>
          );
        }
      }
      last = m.index + tok.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  const blocks = districtSeo.seoText.split(/\n\n+/);
  let imgCounter = 0;
  return (
    <section className="py-12 sm:py-16 bg-gray-50" data-testid={testId}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <article className="prose prose-gray max-w-none">
          {blocks.map((block, i) => {
            const trimmed = block.trim();
            if (trimmed.startsWith("#### ")) {
              return (
                <h3 key={i} className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6">
                  {trimmed.replace(/^####\s+/, "")}
                </h3>
              );
            }
            if (trimmed.startsWith("### ")) {
              return (
                <h2
                  key={i}
                  className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 mt-8 first:mt-0"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  {trimmed.replace(/^###\s+/, "")}
                </h2>
              );
            }
            const im = trimmed.match(IMG_RE);
            if (im) {
              const imgIdx = imgCounter++;
              return (
                <figure key={i} className="my-6" data-testid={`district-seo-image-${imgIdx}`}>
                  <img
                    src={im[2]}
                    alt={im[1]}
                    loading="lazy"
                    className="rounded-xl shadow-sm w-full object-cover"
                  />
                </figure>
              );
            }
            if (trimmed.startsWith("- ")) {
              const items = trimmed.split("\n").filter((l) => l.trim());
              return (
                <ul key={i} className="list-disc pl-6 mb-4 space-y-2 text-gray-700 leading-relaxed">
                  {items.map((l, j) => <li key={j}>{renderInline(l.replace(/^-\s+/, ""))}</li>)}
                </ul>
              );
            }
            if (trimmed.startsWith("|")) {
              const rows = trimmed
                .split("\n")
                .filter((l) => l.trim() && !/^\|?\s*:?-{3,}/.test(l.trim()))
                .map((l) => l.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim()));
              if (!rows.length) return null;
              return (
                <div key={i} className="overflow-x-auto mb-6" data-testid="district-price-table">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-300">
                        {rows[0].map((c, j) => <th key={j} className="py-2 pr-4 font-semibold text-gray-900">{renderInline(c)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.slice(1).map((r, ri) => (
                        <tr key={ri} className="border-b border-gray-200 align-top">
                          {r.map((c, j) => <td key={j} className="py-3 pr-4 text-gray-700">{renderInline(c)}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }
            return (
              <p key={i} className="text-gray-700 leading-relaxed mb-4">
                {renderInline(block)}
              </p>
            );
          })}
        </article>
      </div>
    </section>
  );
}

function DistrictFAQ({ faq, lang, loc }) {
  const [openIndex, setOpenIndex] = useState(null);
  const [items, setItems] = useState(faq && faq.length ? faq : []);

  useEffect(() => {
    if (faq && faq.length) { setItems(faq); return; }
    if (!loc) { setItems([]); return; }
    let active = true;
    axios
      .get(`${API}/faq`, { params: { kind: "district", loc, lang } })
      .then((r) => { if (active) setItems(r.data.items || []); })
      .catch(() => { if (active) setItems([]); });
    return () => { active = false; };
  }, [faq, loc, lang]);

  if (!items || items.length === 0) return null;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const titleByLang = { ru: "Частые вопросы", en: "Frequently Asked Questions", tr: "Sikca Sorulan Sorular" };

  return (
    <section className="py-12 sm:py-16 bg-white" data-testid="district-faq">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-1.5 rounded-full mb-4">
            <HelpCircle className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">FAQ</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "Playfair Display, serif" }}>
            {titleByLang[lang] || titleByLang.ru}
          </h2>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {items.map((item, idx) => (
            <div
              key={idx}
              className={`border-b border-gray-100 last:border-b-0 ${openIndex === idx ? "bg-pink-50/30" : ""}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full text-left px-5 sm:px-6 py-5 flex items-center justify-between gap-4"
                data-testid={`district-faq-toggle-${idx}`}
              >
                <span className="font-semibold text-gray-900">{item.q}</span>
                <span className={`text-pink-500 transition-transform ${openIndex === idx ? "rotate-45" : ""}`}>+</span>
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${openIndex === idx ? "max-h-[600px] pb-5" : "max-h-0"}`}>
                <p className="px-5 sm:px-6 text-sm text-gray-600 leading-relaxed whitespace-pre-line">{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function DistrictPage() {
  const { citySlug, districtSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const city = CITIES[citySlug];
  const district = city?.districts?.find((d) => d.slug === districtSlug);

  const availableLanguages = city?.languages || DEFAULT_LANGS;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortOrder, setSortOrder] = useState("asc");
  const [previewProduct, setPreviewProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [exchangeRates, setExchangeRates] = useState(null);
  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, availableLanguages));

  const setLang = (newLang) => {
    setLangState(newLang);
    setLangInUrl(newLang, searchParams, setSearchParams);
  };

  useAutoSyncLangToUrl(lang, searchParams, setSearchParams);

  useHreflang(`/${citySlug}/${districtSlug}`, availableLanguages);

  // Redirect if invalid
  useEffect(() => {
    if (!city) {
      navigate("/", { replace: true });
    } else if (!district) {
      navigate(`/${citySlug}`, { replace: true });
    }
  }, [city, district, citySlug, navigate]);

  // SEO
  useEffect(() => {
    if (!district) return;
    const seo = district.seo[lang] || district.seo.ru;
    document.title = shortTitle(seo.title);
    const setMeta = (sel, val) => {
      const el = document.querySelector(sel);
      if (el && val) el.setAttribute("content", val);
    };
    setMeta('meta[name="description"]', shortDescription(seo.description));
    setMeta('meta[name="keywords"]', seo.keywords);
    setMeta('meta[property="og:title"]', seo.title);
    setMeta('meta[property="og:description"]', seo.description);
    setMeta('meta[property="og:url"]', `https://yuccaflower.com/${citySlug}/${districtSlug}`);
    setMeta('meta[name="twitter:title"]', seo.title);
    setMeta('meta[name="twitter:description"]', seo.description);
    updateCanonical(`/${citySlug}/${districtSlug}`, availableLanguages);
    document.documentElement.lang = lang;
  }, [district, lang, citySlug, districtSlug, availableLanguages]);

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes, settingsRes, ratesRes] = await Promise.all([
        axios.get(`${API}/products?city=${citySlug}&district=${districtSlug}`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/exchange-rates`),
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setSettings(settingsRes.data);
      setExchangeRates(ratesRes.data.rates);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Ошибка загрузки данных");
    } finally {
      setLoading(false);
    }
  }, [citySlug, districtSlug]);

  useEffect(() => {
    setLoading(true);
    if (city) setSelectedCurrency(city.currency);
    fetchData();
    if (citySlug && districtSlug) trackPageView(`/${citySlug}/${districtSlug}`, citySlug);
  }, [fetchData, city, citySlug, districtSlug]);

  const handleCityChange = (slug) => {
    navigate(`/${slug}`);
  };

  const filteredProducts = useMemo(() => {
    const base = districtCatalog(products, districtSlug, 20);
    const sorted = base.slice().sort((a, b) => (sortOrder === "asc" ? a.price - b.price : b.price - a.price));
    if (selectedCategory !== "all") return sorted.filter((p) => p.category === selectedCategory);
    if (sortOrder === "desc") return sorted;
    const cheapest = sorted[0];
    const rest = sorted.slice(1);
    const standards = rest.filter((p) => p.category === "standard");
    const mediums = rest.filter((p) => p.category === "medium");
    const premiums = rest.filter((p) => p.category === "premium");
    const interleaved = [];
    let si = 0, mi = 0;
    while (si < standards.length || mi < mediums.length) {
      if (si < standards.length) interleaved.push(standards[si++]);
      if (mi < mediums.length) interleaved.push(mediums[mi++]);
    }
    return cheapest ? [cheapest, ...interleaved, ...premiums] : [...interleaved, ...premiums];
  }, [products, districtSlug, selectedCategory, sortOrder]);

  if (!city || !district) return null;

  const cityName = lang === "en" ? city.nameEn : lang === "tr" ? city.nameTr || city.nameEn : city.name;
  const districtPrep = lang === "en" ? district.namePrepositionalEn : lang === "tr" ? district.namePrepositionalTr || district.namePrepositionalEn : district.namePrepositional;
  const districtName = lang === "en" ? district.nameEn : lang === "tr" ? district.nameTr || district.nameEn : district.name;

  const baseCurrency = city.currency;
  const displayCurrency = getCurrencyByCode(selectedCurrency || baseCurrency);
  const cp = (price) => convertPrice(price, baseCurrency, displayCurrency.code, exchangeRates);

  const districtSeo = district.seo[lang] || district.seo.ru;

  // Prefer truly-unique (non-templated) SEO text + FAQ for Sorrento districts
  const uniqueSorrento = citySlug === "sorrento" ? sorrentoDistrictContent[districtSlug]?.[lang] : null;
  // Dubai / St. Moritz: unique long-form blocks + FAQ appended to (not replacing) the existing content
  const extraMap = DISTRICT_EXTRA_CONTENT[citySlug];
  const dubaiExtra = extraMap ? extraMap[districtSlug]?.[lang] || extraMap[districtSlug]?.en : null;
  // Drop base paragraphs/headings/FAQ that are repeated verbatim in the long-form block (no duplicate text on the page)
  const norm = (s) => (s || "").replace(/\s+/g, " ").trim();
  const low = (s) => norm(s).toLowerCase();
  const h1Text = `${t("hero.title", lang)} ${districtPrep}`;
  const isH1Heading = (p) => /^#{1,4}\s/.test(p.trim()) && low(p.replace(/^#+\s*/, "")) === low(h1Text);
  const dedupeSeoText = (base, extra) => {
    if (!base || !extra) return base;
    const extraParas = new Set([...extra.split(/\n\n+/), districtSeo.heroSubtitle || ""].map(norm));
    const kept = base.split(/\n\n+/).filter((p) => !extraParas.has(norm(p)) && !isH1Heading(p));
    return kept.some((p) => !/^#|^\[/.test(p.trim())) ? kept.join("\n\n") : "";
  };
  const stripH1Heading = (text) => (text ? text.split(/\n\n+/).filter((p) => !isH1Heading(p)).join("\n\n") : text);
  const seoText = stripH1Heading(uniqueSorrento?.seoText || dedupeSeoText(districtSeo.seoText, dubaiExtra?.seoText));
  const extraSeoText = stripH1Heading(dubaiExtra?.seoText);
  // FAQ: same question reworded (≥60% shared words) or same answer counts as a duplicate; the long-form version wins
  const tokens = (s) => new Set(low(s).replace(/[^\p{L}\p{N} ]/gu, " ").split(" ").filter((w) => w.length >= 4));
  const similar = (a, b) => {
    const A = tokens(a), B = tokens(b);
    if (!A.size || !B.size) return false;
    let shared = 0;
    A.forEach((w) => B.has(w) && shared++);
    return shared / Math.min(A.size, B.size) >= 0.6;
  };
  const mergeFaq = (base = [], extra = []) => {
    const out = [];
    [...extra, ...base].forEach((f) => {
      if (!out.some((o) => similar(o.q, f.q) || low(o.a) === low(f.a))) out.push(f);
    });
    return out;
  };
  const districtFaq = uniqueSorrento?.faq || (dubaiExtra ? mergeFaq(districtSeo.faq, dubaiExtra.faq) : districtSeo.faq);

  const openWhatsApp = (product = null) => {
    const phone = settings?.whatsapp?.replace(/[^0-9]/g, "") || "905316224378";
    let message;
    if (product) {
      const prodName = lang === "tr" && product.name_tr ? product.name_tr : (lang === "en" && product.name_en ? product.name_en : product.name);
      if (product.category === "premium") {
        message = lang === "tr"
          ? `Merhaba! "${prodName}" icin fiyat ogrenmek istiyorum (${districtName}, ${cityName})`
          : lang === "en"
          ? `Hello! I'd like to know the price for "${prodName}" (${districtName}, ${cityName})`
          : `Здравствуйте! Хочу узнать цену "${prodName}" (${districtName}, ${cityName})`;
      } else {
        message = t("wa.order", lang, { name: prodName, price: `${displayCurrency.symbol}${cp(product.price)}`, city: `${districtName}, ${cityName}` });
      }
    } else {
      message = lang === "tr"
        ? `Merhaba! ${districtPrep} cicek teslimati hakkinda bilgi almak istiyorum.`
        : lang === "en"
        ? `Hello! I'd like to know about flower delivery ${districtPrep}.`
        : `Здравствуйте! Хочу узнать про доставку цветов ${districtPrep}.`;
    }
    trackConversion("whatsapp", product?.name || null);
    trackClick("whatsapp", `${citySlug}/${districtSlug}`);
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#e8b4b8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500 font-medium">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header
        settings={settings}
        currentCity={city}
        onCityChange={handleCityChange}
        selectedCurrency={selectedCurrency || baseCurrency}
        onCurrencyChange={setSelectedCurrency}
        lang={lang}
        onLangChange={setLang}
        t={(key, vars) => t(key, lang, vars)}
        availableLanguages={availableLanguages}
      />

      <main>
        <HeroSection
          city={city}
          onOrderClick={() => openWhatsApp()}
          onWhatsAppClick={() => openWhatsApp()}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
          titleOverride={districtPrep}
          subtitleOverride={districtSeo.heroSubtitle}
        />

        {/* Breadcrumb */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 text-sm text-gray-500" aria-label="Breadcrumb" data-testid="district-breadcrumb">
          <ol className="flex flex-wrap items-center gap-2">
            <li><a href="/" className="hover:text-pink-600">YUCCA Flowers</a></li>
            <li>/</li>
            <li><a href={`/${citySlug}${lang !== "ru" ? `?lang=${lang}` : ""}`} className="hover:text-pink-600">{cityName}</a></li>
            <li>/</li>
            <li className="text-gray-700 font-medium">{districtName}</li>
          </ol>
        </nav>

        <CategoriesSection
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          sortOrder={sortOrder}
          onSortChange={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          currencySymbol={displayCurrency.symbol}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />

        <ProductsSection
          products={filteredProducts}
          onOrderClick={openWhatsApp}
          onImageClick={setPreviewProduct}
          cityName={districtPrep}
          currencySymbol={displayCurrency.symbol}
          convertPrice={cp}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />

        <div className="text-center my-8">
          <a
            href={`/${citySlug}`}
            data-testid="district-full-catalog-link"
            className="inline-block px-6 py-3 rounded-full border border-emerald-600 text-emerald-700 font-medium hover:bg-emerald-50 transition-colors"
          >
            {lang === "en"
              ? `See the full catalog — flower delivery in ${cityName}`
              : lang === "tr"
              ? `Tüm kataloğu gör — ${cityName} çiçek teslimatı`
              : `Смотреть весь каталог — доставка цветов в ${cityName}`}
          </a>
        </div>

        <DistrictSeoBlock districtSeo={{ seoText }} />

        {extraSeoText && <DistrictSeoBlock districtSeo={{ seoText: extraSeoText }} testId="district-extra-content" />}

        <HowWeWorkSection lang={lang} t={(key, vars) => t(key, lang, vars)} />

        <AdvantagesSection lang={lang} t={(key, vars) => t(key, lang, vars)} />

        <GuaranteesSection lang={lang} />

        <ReviewsSection lang={lang} t={(key, vars) => t(key, lang, vars)} />

        <GoogleReviewCta url={city.googleReviewUrl} lang={lang} />

        <DistrictFAQ
          faq={districtFaq}
          lang={lang}
          loc={districtPrep || (lang === "en" ? `in ${districtName}` : lang === "tr" ? `${districtName}'da` : `в ${districtName}`)}
        />
      </main>

      <Footer settings={settings} city={city} lang={lang} t={(key, vars) => t(key, lang, vars)} />

      <DistrictJsonLd citySlug={citySlug} cityName={cityName} district={district} lang={lang} />

      <FloatingWhatsApp onClick={() => openWhatsApp()} />

      {previewProduct && (
        <ProductPreview
          product={previewProduct}
          onClose={() => setPreviewProduct(null)}
          onOrderClick={openWhatsApp}
          currencySymbol={displayCurrency.symbol}
          convertPrice={cp}
          lang={lang}
          t={(key, vars) => t(key, lang, vars)}
        />
      )}
    </>
  );
}
